import {
  SizableText,
  useMedia,
  XStack,
  YStack,
  YStackProps,
} from '@mythical/ui'
import React, { useCallback, useState } from 'react'
import { Pressable, GestureResponderEvent } from 'react-native'

import { Button } from '../primitive/Button'
import {
  IconFaceSad,
  IconFaceHappy,
  IconFaceNeutral,
  IconLock,
  IconChevronLeft,
  IconChevronRight,
} from '../primitive/Icon'

export type RatingTypes = 'happy' | 'neutral' | 'sad'

const ratingIconMap: { [key in RatingTypes]: Icons } = {
  sad: IconFaceSad,
  neutral: IconFaceNeutral,
  happy: IconFaceHappy,
} as const

type Icons = typeof IconFaceSad

type ContentRatingProps = {
  handleRatingPress: (rating: string) => void
  onPreviousPress?: (e: GestureResponderEvent) => void
  onNextPress?: (e: GestureResponderEvent) => void
  nextHref?: string
  previousHref?: string
  currentRating?: RatingTypes
  isLocked?: boolean
  displayRating?: boolean
}

export const ratingMap = {
  sad: -1,
  neutral: 0,
  happy: 1,
}

export const ratingMapReverse = {
  '-1': 'sad',
  '0': 'neutral',
  '1': 'happy',
}

const ContentRating = ({
  handleRatingPress,
  onPreviousPress,
  onNextPress,
  nextHref,
  previousHref,
  currentRating,
  isLocked,
  displayRating,
}: ContentRatingProps) => {
  const [hasRated, setHasRated] = useState<RatingTypes | null>(null)
  const media = useMedia()
  const handleRatingPressWrapper = useCallback(
    (rating: RatingTypes) => {
      setHasRated(rating)
      handleRatingPress(rating)
    },
    [handleRatingPress]
  )

  return (
    <YStack
      borderTopWidth={1}
      borderBottomWidth={1}
      borderColor="$secondary8"
      space="$3"
      jc="space-between"
      ai="center"
      py="$3"
    >
      {!media.gtXs && displayRating && (
        <RatingPanel
          display="flex"
          isLocked={isLocked}
          currentRating={hasRated || currentRating}
          handleRatingPress={handleRatingPressWrapper}
          hasRated={!!hasRated}
        />
      )}

      <XStack
        space="$2"
        jc="center"
        ai="center"
        w="100%"
        $gtXs={{
          jc: 'space-between',
        }}
      >
        <Button
          pl="$2"
          scaleSpace={0.2}
          disabled={!previousHref}
          theme="tertiary"
          icon={<IconChevronLeft size={28} color="$primary6" />}
          onPress={onPreviousPress}
        >
          PREVIOUS
        </Button>
        {media.gtXs && displayRating && (
          <RatingPanel
            handleRatingPress={handleRatingPressWrapper}
            currentRating={hasRated || currentRating}
            isLocked={isLocked}
            hasRated={!!hasRated}
          />
        )}
        <Button
          pr="$2"
          scaleSpace={0.2}
          theme="tertiary"
          disabled={!nextHref}
          iconAfter={<IconChevronRight size={28} color="$primary6" />}
          onPress={onNextPress}
        >
          NEXT
        </Button>
      </XStack>
    </YStack>
  )
}

export default ContentRating

type RatingPanelProps = {
  handleRatingPress: ContentRatingProps['handleRatingPress']
  currentRating?: ContentRatingProps['currentRating']
  hasRated?: boolean
  isLocked?: boolean
}

const RatingPanel = ({
  handleRatingPress,
  currentRating,
  isLocked,
  hasRated,
  ...props
}: RatingPanelProps & YStackProps) => {
  if (isLocked) {
    return <RatingLocked {...props} />
  }
  return (
    <YStack jc="center" py="$2" space="$2" {...props}>
      <SizableText textAlign="center" size="$5" color="$primary10">
        {hasRated ? 'Thanks for rating!' : 'Rate this video!'}
      </SizableText>
      <XStack justifyContent="center" space="$3">
        {Object.keys(ratingIconMap).map((rating) => (
          <RatingButton
            key={rating}
            isActive={rating === currentRating}
            onPress={() => handleRatingPress(rating)}
            icon={ratingIconMap[rating] as Icons}
          />
        ))}
      </XStack>
    </YStack>
  )
}

type RatingButtonProps = {
  icon: Icons
  isActive?: boolean
  onPress: () => void
}

const RatingLocked = ({ ...props }: YStackProps) => {
  return (
    <YStack ai="center" jc="center" space="$2" {...props}>
      <IconLock color="$primary10" />
      <SizableText textAlign="center" size="$5" color="$primary10">
        Rating locked
      </SizableText>
    </YStack>
  )
}

const RatingButton = ({ icon, onPress, isActive }: RatingButtonProps) => {
  const Icon = icon
  return (
    <Pressable onPress={onPress}>
      <YStack
        hoverStyle={{ scale: isActive ? 1.18 : 1.05 }}
        padding="$0.25"
        animation="bouncy"
        scale={isActive ? 1.1 : 1}
        borderWidth={2}
        borderRadius={9999}
        borderColor={isActive ? '$primary10' : 'transparent'}
      >
        <Icon size={34} />
      </YStack>
    </Pressable>
  )
}
